/**
 * Breakout Game
 * Destroy bricks with a bouncing ball
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Breakout = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('breakout_score', 0);
  const [highScore, setHighScore] = useLocalStorage('breakout_highScore', 0);
  const gameStateRef = useRef({
    paddle: { x: 175, y: 570, width: 50, height: 10 },
    ball: { x: 200, y: 400, vx: 3, vy: -3, radius: 5 },
    bricks: [],
    lives: 3,
  });

  const CANVAS_WIDTH = 400;
  const CANVAS_HEIGHT = 600;

  const initBricks = () => {
    const bricks = [];
    const cols = 8;
    const rows = 4;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        bricks.push({
          x: c * 50,
          y: r * 20 + 20,
          width: 48,
          height: 18,
          active: true,
        });
      }
    }
    return bricks;
  };

  const startGame = () => {
    gameStateRef.current = {
      paddle: { x: 175, y: 570, width: 50, height: 10 },
      ball: { x: 200, y: 400, vx: 3, vy: -3, radius: 5 },
      bricks: initBricks(),
      lives: 3,
    };
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;

    const gameLoop = setInterval(() => {
      // Clear canvas
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Update ball
      game.ball.x += game.ball.vx;
      game.ball.y += game.ball.vy;

      // Ball collision with walls
      if (game.ball.x - game.ball.radius < 0 || game.ball.x + game.ball.radius > CANVAS_WIDTH) {
        game.ball.vx *= -1;
      }
      if (game.ball.y - game.ball.radius < 0) {
        game.ball.vy *= -1;
      }

      // Ball collision with paddle
      if (
        game.ball.x > game.paddle.x &&
        game.ball.x < game.paddle.x + game.paddle.width &&
        game.ball.y + game.ball.radius > game.paddle.y &&
        game.ball.y < game.paddle.y + game.paddle.height
      ) {
        game.ball.vy *= -1;
        setScore((prev) => prev + 10);
      }

      // Ball lost
      if (game.ball.y > CANVAS_HEIGHT) {
        game.lives--;
        if (game.lives <= 0) {
          setGameState(GAME_CONFIG.states.GAME_OVER);
          if (score > highScore) setHighScore(score);
        } else {
          game.ball = { x: 200, y: 400, vx: 3, vy: -3, radius: 5 };
        }
      }

      // Brick collision
      game.bricks.forEach((brick) => {
        if (!brick.active) return;
        if (
          game.ball.x > brick.x &&
          game.ball.x < brick.x + brick.width &&
          game.ball.y > brick.y &&
          game.ball.y < brick.y + brick.height
        ) {
          brick.active = false;
          game.ball.vy *= -1;
          setScore((prev) => prev + 50);
        }
      });

      // Draw paddle
      ctx.fillStyle = GAME_CONFIG.colors.accent;
      ctx.fillRect(game.paddle.x, game.paddle.y, game.paddle.width, game.paddle.height);

      // Draw ball
      ctx.fillStyle = GAME_CONFIG.colors.accent2;
      ctx.beginPath();
      ctx.arc(game.ball.x, game.ball.y, game.ball.radius, 0, Math.PI * 2);
      ctx.fill();

      // Draw bricks
      game.bricks.forEach((brick) => {
        if (brick.active) {
          ctx.fillStyle = GAME_CONFIG.colors.primary;
          ctx.fillRect(brick.x, brick.y, brick.width, brick.height);
        }
      });
    }, 30);

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      game.paddle.x = Math.max(0, Math.min(x - game.paddle.width / 2, CANVAS_WIDTH - game.paddle.width));
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      clearInterval(gameLoop);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [gameState, score, highScore]);

  return (
    <GameLayout
      title="Breakout 🧱"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Breakout</h2>
            <p style={styles.menuDescription}>
              Destroy all the bricks with your bouncing ball!
            </p>
            <button
              onClick={startGame}
              className="btn btn-primary btn-lg"
              style={{ marginTop: '1rem' }}
            >
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              style={styles.canvas}
            />
            <div style={styles.instructions}>
              Move mouse left/right to control paddle. Destroy all bricks!
            </div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>Game Over</h1>
            <ScoreDisplay score={score} stats={{ 'Final Score': score }} />
            <button
              onClick={startGame}
              className="btn btn-primary mt-3"
              style={{ width: '100%' }}
            >
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    fontSize: '1rem',
  },
  canvas: {
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
    backgroundColor: '#000',
    display: 'block',
  },
  instructions: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default Breakout;
